import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { AudioPlayState } from "@/manager/manager/audio-controller-manager";
import { AudioSearchForm } from "./audio-search-form";
import { AudioDataList, AudioDataListRef } from "./audio-data-list";
import { AudioDataCard } from "./audio-data-card";

// 表格行骨架屏组件
const TableRowSkeleton: React.FC = () => {
  return (
    <tr className="border-b">
      <td className="px-6 py-5">
        <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="px-6 py-5">
        <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="px-6 py-5">
        <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="px-6 py-5">
        <div className="w-14 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="p-4">
        <div className="flex items-center justify-center gap-2">
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </td>
    </tr>
  );
};

interface AudioTableProps {
  audios: Audio[];
  selectAudio?: Audio;
  playingAudio?: Audio;
  currentTime: number;
  duration: number;
  playState: AudioPlayState;
  searchName: string;
  condition: string;
  filters: Record<string, string>;
  filterOptions: Record<string, { label: string; value: string }[]>;
  loading: boolean;
  onSearchNameChange: (value: string) => void;
  onConditionChange: (value: string) => void;
  onFilterChange: (key: string, value: string) => void;
  onVoiceRename: (voice: Audio, name: string) => void;
  onVoiceAdd: (voice: Audio) => void;
  onVoiceSelect: (voice: Audio) => void;
  onPlayPause: (voice: Audio) => void;
  onToggleFavorite: (voice: Audio) => void;
}

// 添加ref接口
export interface AudioTableRef {
  scrollToCurrentAudio: () => void;
}

export const AudioTable = forwardRef<AudioTableRef, AudioTableProps>((props, ref) => {
  const {
    audios,
    selectAudio,
    playingAudio,
    currentTime,
    duration,
    playState,
    searchName,
    condition,
    filters,
    filterOptions,
    loading,
    onSearchNameChange,
    onConditionChange,
    onFilterChange,
    onVoiceRename,
    onVoiceAdd,
    onVoiceSelect,
    onPlayPause,
    onToggleFavorite,
  } = props;
  const audioDataRef = useRef<AudioDataListRef>(null);

  // 使用 useImperativeHandle 确保外部 ref 能够访问到 AudioDataList 的方法
  useImperativeHandle(ref, () => ({
    scrollToCurrentAudio: () => {
      if (audioDataRef.current) {
        audioDataRef.current.scrollToCurrentAudio();
      }
    },
  }));

  return (
    <div className="flex-1 flex flex-col mt-6 mx-6">
      {/* 筛选器 */}
      <AudioSearchForm
        searchName={searchName}
        condition={condition}
        filters={filters}
        filterOptions={filterOptions}
        onSearchNameChange={onSearchNameChange}
        onConditionChange={onConditionChange}
        onFilterChange={onFilterChange}
      />
      {condition === "my_voices" && (
        <AudioDataCard
          ref={audioDataRef}
          loading={loading}
          audios={audios}
          selectAudio={selectAudio}
          playingAudio={playingAudio}
          currentTime={currentTime}
          duration={duration}
          playState={playState}
          onVoiceRename={onVoiceRename}
          onVoiceAdd={onVoiceAdd}
          onVoiceSelect={onVoiceSelect}
          onPlayPause={onPlayPause}
          onToggleFavorite={onToggleFavorite}
        />
      )}
      {condition === "my_voices" || (
        // 音频表格
        <AudioDataList
          ref={audioDataRef}
          loading={loading}
          audios={audios}
          selectAudio={selectAudio}
          playingAudio={playingAudio}
          currentTime={currentTime}
          duration={duration}
          playState={playState}
          onVoiceSelect={onVoiceSelect}
          onPlayPause={onPlayPause}
          onToggleFavorite={onToggleFavorite}
        />
      )}
    </div>
  );
});
